import { useState, useEffect } from 'react';
import { Card, Tabs, Table, Button, Tooltip, Alert } from 'antd';
import { InfoCircleFilled, RightOutlined } from '@ant-design/icons';
import { Bar, BarConfig } from '@ant-design/plots';
import { useBusinessFunctionsItAppSummary } from '../hooks/useBusinessFunctionsItAppSummary';
import { LoadingSpinner } from './LoadingSpinner';
import { tableSortingMethods } from '../../../utils';
import { ColumnType } from 'antd/es/table';
import { useQualitativeFactorsItAppSummary } from '../hooks/useQualitativeFactorsItAppSummary';
import { useProcessAreasItAppSummary } from '../hooks/useProcessAreasItAppSummary';
import { useLocationItAppSummary } from '../hooks/useLocationItAppSummary';
import { useNavigate } from 'react-router';

const { TabPane } = Tabs;

// Tab constants
const TAB_LOCATIONS = 'Locations';
const TAB_BUSINESS_FUNCTIONS = 'Business Functions';
const TAB_QUALITATIVE_FACTORS = 'Qualitative Factors';
const TAB_PROCESS_AREAS = 'Process Areas';
const TAB_HOSTING_ENTITIES = 'Hosting Entities';

// Tooltips
const TAB_BUSINESS_FUNCTIONS_TOOLTIP =
  'List of Business Functions and their IT Applications';

const TAB_QUALITATIVE_FACTORS_TOOLTIP =
  'List of Qualitative Factors and linked IT Applications.';

const TAB_PROCESS_AREAS_TOOLTIP =
  'List of Process Areas and their IT Applications.';

const TAB_LOCATIONS_TOOLTIP = 'IT Applications per company Location.';

type CyberHubTabsProps = {
  locationUuid: string;
};

type CyberHubTabsDataSource = {
  name: string;
  applicationsCount: number;
  uuid?: string;
};

export const CyberHubTabs = ({ locationUuid }: CyberHubTabsProps) => {
  // Determine which tab should be the first tab based on locationUuid
  const firstTab = locationUuid ? TAB_PROCESS_AREAS : TAB_LOCATIONS;
  const [internalActiveTab, setInternalActiveTab] = useState(firstTab);

  const navigate = useNavigate();

  const {
    data: businessFunctionsItAppSummary,
    loading,
    error,
  } = useBusinessFunctionsItAppSummary(locationUuid);

  const {
    data: qualitativeFactorsItAppSummary,
    loading: qualitativeFactorsItAppSummaryLoading,
    error: qualitativeFactorsItAppSummaryError,
  } = useQualitativeFactorsItAppSummary(locationUuid);

  const {
    data: processAreasItAppSummary,
    loading: processAreasItAppSummaryLoading,
    error: processAreasItAppSummaryError,
  } = useProcessAreasItAppSummary(locationUuid);

  const {
    data: locationSummary,
    loading: locationSummaryLoading,
    error: locationSummaryError,
  } = useLocationItAppSummary();
  // Ensure the first tab is selected when component mounts or locationUuid changes
  useEffect(() => {
    const firstTab = locationUuid ? TAB_PROCESS_AREAS : TAB_LOCATIONS;
    setInternalActiveTab(firstTab);
  }, [locationUuid]);

  const handleTabChange = (key: string) => {
    setInternalActiveTab(key);
  };

  const getDataSource = (tabKey: string): CyberHubTabsDataSource[] => {
    switch (tabKey) {
      case TAB_LOCATIONS:
        if (locationSummary) {
          return locationSummary.map((item) => ({
            uuid: item.uuid,
            name: item.name,
            applicationsCount: item.applicationsCount,
          }));
        }
        return [];
      case TAB_BUSINESS_FUNCTIONS:
        if (businessFunctionsItAppSummary) {
          return businessFunctionsItAppSummary.map((item) => ({
            uuid: item.uuid,
            name: item.name,
            applicationsCount: item.applicationsCount,
          }));
        }
        return [];
      case TAB_QUALITATIVE_FACTORS:
        if (qualitativeFactorsItAppSummary) {
          return qualitativeFactorsItAppSummary.map((item) => ({
            uuid: item.uuid,
            name: item.name,
            applicationsCount: item.applicationsCount,
          }));
        }
        return [];
      case TAB_HOSTING_ENTITIES:
        return [];
      case TAB_PROCESS_AREAS:
        if (processAreasItAppSummary) {
          return processAreasItAppSummary.map((item) => ({
            uuid: item.uuid,
            name: item.name,
            applicationsCount: item.applicationsCount,
          }));
        }
        return [];
      default:
        return locationUuid
          ? processAreasItAppSummary?.map((item) => ({
              uuid: item.uuid,
              name: item.name,
              applicationsCount: item.applicationsCount,
            })) || []
          : [];
    }
  };

  // Calculate maxApplications for the current tab
  const currentDataSource = getDataSource(internalActiveTab);
  const maxApplications = Math.max(
    ...currentDataSource.map(
      (item: { applicationsCount: number }) => item.applicationsCount,
    ),
    0, // Ensure there's at least a 0 if the list is empty or all apps are 0
  );

  const tableColumns = [
    {
      title: (() => {
        switch (internalActiveTab) {
          case TAB_LOCATIONS:
            return 'Location';
          case TAB_BUSINESS_FUNCTIONS:
            return 'Business Function';
          case TAB_QUALITATIVE_FACTORS:
            return 'Qualitative Factor';
          case TAB_PROCESS_AREAS:
            return 'Process Area';
          case TAB_HOSTING_ENTITIES:
            return 'Hosting Entity';
          default:
            return locationUuid ? 'Process Area' : 'Location';
        }
      })(),
      dataIndex: 'name',
      key: 'name',
      sorter: (a: { name: string }, b: { name: string }) =>
        tableSortingMethods.string(a.name, b.name),
      sortDirections: ['descend', 'ascend'],
      defaultSortOrder: 'ascend',
      render: (nameText: string) => (
        <div className="flex items-center">
          <div className="w-[200px] mr-4">
            <span className="font-medium">{nameText}</span>
          </div>
        </div>
      ),
    },
    {
      title: 'Number of Applications',
      dataIndex: 'applicationsCount',
      key: 'applicationsCount',
      render: (
        num: number,
        record: { name: string; applicationsCount: number },
      ) => {
        const barConfig: BarConfig = {
          data: [{ category: record.name, value: num }],
          xField: 'value',
          yField: 'category',
          xAxis: {
            min: 0,
            max: maxApplications > 0 ? maxApplications : 1,
            line: null,
            grid: null,
            label: null,
            title: null,
            tickLine: null,
          },
          yAxis: false,
          legend: false,
          tooltip: false,
          label: false,
          barStyle: {
            fill: '#2D5BFF',
            radius: 8,
          },
          height: 26,
          padding: 0,
        };
        return (
          <div className="flex flex-row items-center gap-[16px]">
            <div
              style={{
                width: '300px',
                height: '26px',
              }}
            >
              <Bar {...barConfig} />
            </div>
            <div className="flex flex-row items-center gap-2">
              <span className="font-semibold">{num}</span>
              <span>Applications</span>
            </div>
          </div>
        );
      },
    },
    {
      title: '',
      key: 'action',
      width: 100,
      render: (_record: CyberHubTabsDataSource) => {
        const getNavigationUrl = () => {
          switch (internalActiveTab) {
            case TAB_BUSINESS_FUNCTIONS:
              return `/cyber-hub/business-functions-it-applications?businessFunctionId=${
                _record.uuid
              }${locationUuid ? `&locationId=${locationUuid}` : ''}`;
            case TAB_QUALITATIVE_FACTORS:
              return `/cyber-hub/qualitative-factors-it-applications?qualitativeFactorId=${
                _record.uuid
              }${locationUuid ? `&locationId=${locationUuid}` : ''}`;
            case TAB_PROCESS_AREAS:
              return `/cyber-hub/process-areas-it-applications?processAreaId=${
                _record.uuid
              }${locationUuid ? `&locationId=${locationUuid}` : ''}`;
            case TAB_LOCATIONS:
              return `/cyber-hub/locations-it-applications?locationId=${_record.uuid}`;
            case TAB_HOSTING_ENTITIES:
              return `/cyber-hub/hosting-entities-it-applications?hostingEntityId=${
                _record.uuid
              }${locationUuid ? `&locationId=${locationUuid}` : ''}`;
            default:
              return '#';
          }
        };

        const isNavigationAvailable = () => {
          // Only enable navigation for tabs that have available pages
          switch (internalActiveTab) {
            case TAB_BUSINESS_FUNCTIONS:
            case TAB_QUALITATIVE_FACTORS:
            case TAB_PROCESS_AREAS:
              return true;
            case TAB_LOCATIONS:
            case TAB_HOSTING_ENTITIES:
              // These can be enabled when their respective pages are implemented
              return false;
            default:
              return false;
          }
        };

        return (
          <div className="flex justify-end">
            <Button
              type="link"
              className="text-[#2D5BFF] flex items-center"
              disabled={
                !isNavigationAvailable() || _record.applicationsCount === 0
              }
              style={{
                lineHeight: 1,
                height: 'auto',
                display: 'flex',
                alignItems: 'center',
              }}
              onClick={() => {
                const url = getNavigationUrl();
                if (url !== '#') {
                  navigate(url);
                }
              }}
            >
              View <RightOutlined />
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <Card
        className="h-full rounded-lg border border-[#CDCDCD]"
        headStyle={{
          padding: '0 16px',
          backgroundColor: '#F8F8F8',
        }}
        bodyStyle={{
          padding: '16px',
          paddingTop: '0px',
        }}
        bordered={false}
      >
        <Tabs
          activeKey={internalActiveTab}
          defaultActiveKey={firstTab}
          onChange={handleTabChange}
          className="custom-tabs"
          style={{
            marginTop: '12px',
          }}
        >
          {/* Conditionally render either Locations or Process Areas as the first tab */}
          {locationUuid ? (
            <TabPane
              tab={
                <div className="flex items-center gap-2">
                  <span className="font-semibold">{TAB_PROCESS_AREAS}</span>
                  <Tooltip title={TAB_PROCESS_AREAS_TOOLTIP}>
                    <InfoCircleFilled
                      style={{ marginRight: '0px' }}
                      className={`${
                        internalActiveTab === TAB_PROCESS_AREAS
                          ? 'text-[#293241]'
                          : 'text-white'
                      }`}
                    />
                  </Tooltip>
                </div>
              }
              key={TAB_PROCESS_AREAS}
            >
              <div className="tab-content-wrapper" style={{ height: '500px' }}>
                {processAreasItAppSummaryLoading &&
                internalActiveTab === TAB_PROCESS_AREAS ? (
                  <LoadingSpinner />
                ) : processAreasItAppSummaryError &&
                  internalActiveTab === TAB_PROCESS_AREAS ? (
                  <div className="flex items-center justify-center h-full">
                    <Alert
                      message="Error loading process areas data"
                      description={processAreasItAppSummaryError.message}
                      type="error"
                      showIcon
                    />
                  </div>
                ) : (
                  <Table
                    columns={
                      tableColumns as ColumnType<CyberHubTabsDataSource>[]
                    }
                    dataSource={getDataSource(TAB_PROCESS_AREAS)}
                    pagination={false}
                    className="custom-table"
                    rowKey="name"
                    rowClassName={(_record, index) =>
                      index % 2 === 0 ? 'bg-gray-50' : ''
                    }
                    style={{
                      border: 'none',
                    }}
                    scroll={{ x: 1000, y: 400 }}
                  />
                )}
              </div>
            </TabPane>
          ) : (
            <TabPane
              tab={
                <div className="flex items-center gap-2">
                  <span className="font-semibold">{TAB_LOCATIONS}</span>
                  <Tooltip title={TAB_LOCATIONS_TOOLTIP}>
                    <InfoCircleFilled
                      style={{ marginRight: '0px' }}
                      className={`${
                        internalActiveTab === TAB_LOCATIONS
                          ? 'text-[#293241]'
                          : 'text-white'
                      }`}
                    />
                  </Tooltip>
                </div>
              }
              key={TAB_LOCATIONS}
            >
              <div className="tab-content-wrapper" style={{ height: '500px' }}>
                {locationSummaryLoading &&
                internalActiveTab === TAB_LOCATIONS ? (
                  <LoadingSpinner />
                ) : locationSummaryError &&
                  internalActiveTab === TAB_LOCATIONS ? (
                  <div className="flex items-center justify-center h-full">
                    <Alert
                      message="Error loading locations data"
                      description={locationSummaryError.message}
                      type="error"
                      showIcon
                    />
                  </div>
                ) : (
                  <Table
                    columns={
                      tableColumns as ColumnType<CyberHubTabsDataSource>[]
                    }
                    dataSource={getDataSource(TAB_LOCATIONS)}
                    pagination={false}
                    className="custom-table"
                    rowKey="name"
                    rowClassName={(_record, index) =>
                      index % 2 === 0 ? 'bg-gray-50' : ''
                    }
                    style={{
                      border: 'none',
                    }}
                    scroll={{ x: 1000, y: 400 }}
                  />
                )}
              </div>
            </TabPane>
          )}

          {/* Business Functions Tab */}
          <TabPane
            tab={
              <div className="flex items-center gap-2">
                <span className="font-semibold">{TAB_BUSINESS_FUNCTIONS}</span>
                <Tooltip title={TAB_BUSINESS_FUNCTIONS_TOOLTIP}>
                  <InfoCircleFilled
                    style={{ marginRight: '0px' }}
                    className={`${
                      internalActiveTab === TAB_BUSINESS_FUNCTIONS
                        ? 'text-[#293241]'
                        : 'text-white'
                    }`}
                  />
                </Tooltip>
              </div>
            }
            key={TAB_BUSINESS_FUNCTIONS}
          >
            <div className="tab-content-wrapper" style={{ height: '500px' }}>
              {loading && internalActiveTab === TAB_BUSINESS_FUNCTIONS ? (
                <LoadingSpinner />
              ) : error && internalActiveTab === TAB_BUSINESS_FUNCTIONS ? (
                <div className="flex items-center justify-center h-full">
                  <Alert
                    message="Error loading business functions data"
                    description={error.message}
                    type="error"
                    showIcon
                  />
                </div>
              ) : (
                <Table
                  columns={
                    tableColumns as ColumnType<{
                      name: string;
                      applicationsCount: number;
                    }>[]
                  }
                  dataSource={getDataSource(TAB_BUSINESS_FUNCTIONS)}
                  pagination={false}
                  className="custom-table"
                  rowKey="name"
                  rowClassName={(_record, index) =>
                    index % 2 === 0 ? 'bg-gray-50' : ''
                  }
                  style={{
                    border: 'none',
                  }}
                  scroll={{ x: 1000, y: 400 }}
                />
              )}
            </div>
          </TabPane>

          {/* Qualitative Factors Tab */}
          <TabPane
            tab={
              <div className="flex items-center gap-2">
                <span className="font-semibold">{TAB_QUALITATIVE_FACTORS}</span>
                <Tooltip title={TAB_QUALITATIVE_FACTORS_TOOLTIP}>
                  <InfoCircleFilled
                    style={{ marginRight: '0px' }}
                    className={`${
                      internalActiveTab === TAB_QUALITATIVE_FACTORS
                        ? 'text-[#293241]'
                        : 'text-white'
                    }`}
                  />
                </Tooltip>
              </div>
            }
            key={TAB_QUALITATIVE_FACTORS}
          >
            <div className="tab-content-wrapper" style={{ height: '500px' }}>
              {qualitativeFactorsItAppSummaryLoading &&
              internalActiveTab === TAB_QUALITATIVE_FACTORS ? (
                <LoadingSpinner />
              ) : qualitativeFactorsItAppSummaryError &&
                internalActiveTab === TAB_QUALITATIVE_FACTORS ? (
                <div className="flex items-center justify-center h-full">
                  <Alert
                    message="Error loading qualitative factors data"
                    description={qualitativeFactorsItAppSummaryError.message}
                    type="error"
                    showIcon
                  />
                </div>
              ) : (
                <Table
                  columns={tableColumns as ColumnType<CyberHubTabsDataSource>[]}
                  dataSource={getDataSource(TAB_QUALITATIVE_FACTORS)}
                  pagination={false}
                  className="custom-table"
                  rowKey="name"
                  rowClassName={(_record, index) =>
                    index % 2 === 0 ? 'bg-gray-50' : ''
                  }
                  style={{
                    border: 'none',
                  }}
                  scroll={{ x: 1000, y: 400 }}
                />
              )}
            </div>
          </TabPane>

          {/* Hosting Entities Tab */}
          <TabPane
            tab={
              <div className="flex items-center gap-2">
                <span className="font-semibold">{TAB_HOSTING_ENTITIES}</span>
                <Tooltip title={TAB_HOSTING_ENTITIES}>
                  <InfoCircleFilled
                    style={{ marginRight: '0px' }}
                    className={`${
                      internalActiveTab === TAB_HOSTING_ENTITIES
                        ? 'text-[#293241]'
                        : 'text-white'
                    }`}
                  />
                </Tooltip>
              </div>
            }
            key={TAB_HOSTING_ENTITIES}
          >
            <div className="tab-content-wrapper" style={{ height: '500px' }}>
              <Table
                columns={tableColumns as ColumnType<CyberHubTabsDataSource>[]}
                dataSource={getDataSource(TAB_HOSTING_ENTITIES)}
                pagination={false}
                className="custom-table"
                rowKey="name"
                rowClassName={(_record, index) =>
                  index % 2 === 0 ? 'bg-gray-50' : ''
                }
                style={{
                  border: 'none',
                }}
                scroll={{ x: 1000, y: 400 }}
              />
            </div>
          </TabPane>
        </Tabs>
      </Card>

      {/* Custom styling */}
      <style>
        {`
          /* Custom tab styling to match Figma design */
          .custom-tabs .ant-tabs-nav {
            margin-bottom: 12px !important;
          }

          .custom-tabs .ant-tabs-nav::before {
            border-bottom: none !important;
          }

          .custom-tabs .ant-tabs-tab {
            padding: 8px 16px !important;
            margin: 0 8px !important;
            border-radius: 50px !important;
            font-size: 14px !important;
            background-color: transparent !important;
            border: none !important;
            position: relative;
            transition: all 0.3s ease;
            white-space: nowrap;
          }

          .custom-tabs .ant-tabs-tab + .ant-tabs-tab {
            margin-left: 16px !important;
          }

          .custom-tabs .ant-tabs-tab:not(:last-child)::after {
            content: "|";
            position: absolute;
            right: -12px;
            top: 50%;
            transform: translateY(-50%);
            color: #9CA3AF;
            font-size: 14px;
            font-weight: normal;
          }

          .custom-tabs .ant-tabs-nav-list {
            display: flex;
            align-items: center;
          }

          .custom-tabs .ant-tabs-tab-active {
            background-color: #4B5563 !important;
            border-radius: 50px !important;
          }

          .custom-tabs .ant-tabs-tab-active .ant-tabs-tab-btn {
            color: white !important;
            font-weight: 500 !important;
          }

          .custom-tabs .ant-tabs-tab-active .anticon {
            color: white !important;
          }

          .custom-tabs .ant-tabs-tab:not(.ant-tabs-tab-active) .ant-tabs-tab-btn {
            color: #374151 !important;
            font-weight: 500 !important;
          }

          .custom-tabs .ant-tabs-tab:not(.ant-tabs-tab-active) .anticon {
            color: #6B7280 !important;
          }

          .custom-tabs .ant-tabs-ink-bar {
            display: none !important;
          }

          .custom-tabs .ant-tabs-content-holder {
            border-top: 1px solid #E5E7EB;
            padding-top: 16px;
          }

          .custom-table .ant-table-thead > tr > th {
            background-color: #f5f7fa;
            font-weight: 500;
            padding: 12px 16px;
          }
          .custom-table .ant-table-tbody > tr > td {
            padding: 16px;
          }
          .custom-table .ant-table-tbody > tr:hover > td {
            background-color: #f5f7fa;
          }
          .custom-table .ant-table-cell {
            border-bottom: 1px solid #f0f0f0;
          }
          .tab-content-wrapper {
            min-height: 500px;
            display: flex;
            flex-direction: column;
          }
        `}
      </style>
    </>
  );
};
